import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Checkroom,
  School,
  Edit,
  Save,
  Cancel
} from '@mui/icons-material';
import apiClient from '../../config/axios';

const ProfileInformation = ({ user, updateUser, setError, setSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    telephone: user?.telephone || '',
    telephoneSecondary: user?.telephoneSecondary || '',
    shirtSize: user?.shirtSize || '',
    level: user?.level === 0 ? 'Da assegnare' : user?.level >= 6 ? 'Oltre il 5° anno' : user?.level ? `${user.level}° anno` : '',
    avatar: user?.avatar || '',
    flagPicture: user?.flagPicture
  });

  const shirtSizes = ['Da assegnare', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const levelOptions = ['Da assegnare', '1° anno', '2° anno', '3° anno', '4° anno', '5° anno', 'Oltre il 5° anno'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: user?.name || '',
      surname: user?.surname || '',
      telephone: user?.telephone || '',
      telephoneSecondary: user?.telephoneSecondary || '',
      shirtSize: user?.shirtSize || '',
      level: user?.level === 0 ? 'Da assegnare' : user?.level >= 6 ? 'Oltre il 5° anno' : user?.level ? `${user.level}° anno` : '',
      avatar: user?.avatar || '',
      flagPicture: user?.flagPicture
    });
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Convert level from "X° anno" format to number
      const dataToSend = { ...formData };
      if (dataToSend.level) {
        if (dataToSend.level === 'Da assegnare') {
          dataToSend.level = 0;
        } else if (dataToSend.level === 'Oltre il 5° anno') {
          dataToSend.level = 6; // Assegna 6 per "Oltre il 5° anno"
        } else {
          dataToSend.level = parseInt(dataToSend.level.split('°')[0]);
        }
      }

      const response = await apiClient.patch('/api/user/update-user', dataToSend);
      updateUser({ ...user, ...response.data });
      setSuccess('Profilo aggiornato con successo!');
      setIsEditing(false);
    } catch (error) {
      console.log(error);
      setError(error.response?.data?.errors?.length > 0 ? error.response?.data?.errors[0].msg : 'Errore durante l\'aggiornamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      {/* Header - visible on desktop, hidden on mobile */}
      <Box sx={{ 
        display: { xs: 'none', md: 'flex' }, 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h6">
          Informazioni Personali
        </Typography>
        {!isEditing ? (
          <Button
            startIcon={<Edit />}
            onClick={handleEdit}
            variant="outlined"
          >
            Modifica
          </Button>
        ) : (
          <Box>
            <Button
              startIcon={<Save />}
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              sx={{ mr: 1 }}
            >
              {loading ? <CircularProgress size={20} /> : 'Salva'}
            </Button>
            <Button
              startIcon={<Cancel />}
              onClick={handleCancel}
              variant="outlined"
            >
              Annulla
            </Button>
          </Box>
        )}
      </Box>

      {/* Mobile header - only title, no buttons */}
      <Box sx={{ 
        display: { xs: 'block', md: 'none' }, 
        mb: 3 
      }}>
        <Typography variant="h6">
          Informazioni Personali
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Nome"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={!isEditing}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Cognome"
            name="surname"
            value={formData.surname}
            onChange={handleChange}
            disabled={!isEditing}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Email"
            value={user.email}
            disabled
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Telefono"
            name="telephone"
            value={formData.telephone}
            onChange={handleChange}
            disabled={!isEditing}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Telefono Secondario"
            name="telephoneSecondary"
            value={formData.telephoneSecondary}
            onChange={handleChange}
            disabled={!isEditing}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            select
            label="Taglia Maglietta"
            name="shirtSize"
            value={formData.shirtSize}
            onChange={handleChange}
            disabled={!isEditing}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Checkroom color="action" />
                  </InputAdornment>
                ),
              },
            }}
          >
            {shirtSizes.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            select
            label="Anno di Corso"
            name="level"
            value={formData.level}
            onChange={handleChange}
            disabled={!isEditing}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <School color="action" />
                  </InputAdornment>
                ),
              },
            }}
          >
            {levelOptions.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="flagPicture"
                checked={formData.flagPicture}
                onChange={handleChange}
                disabled={!isEditing}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Liberatoria Foto {formData.flagPicture ? 'Accettata' : 'Non accettata'}
              </Typography>
            }
          />
        </Grid>
      </Grid>

      {/* Mobile buttons - visible only on mobile, positioned at bottom */}
      <Box sx={{ 
        display: { xs: 'flex', md: 'none' }, 
        justifyContent: 'center', 
        mt: 3,
        gap: 2
      }}>
        {!isEditing ? (
          <Button
            startIcon={<Edit />}
            onClick={handleEdit}
            variant="outlined"
            fullWidth
          >
            Modifica
          </Button>
        ) : (
          <>
            <Button
              startIcon={<Save />}
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? <CircularProgress size={20} /> : 'Salva'}
            </Button>
            <Button
              startIcon={<Cancel />}
              onClick={handleCancel}
              variant="outlined"
              sx={{ flex: 1 }}
            >
              Annulla
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default ProfileInformation; 